import { HStack, Icon, RippleButton, Text, VStack } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../hooks/useIsMobile";
import { TestModal } from "../TestModal";
import styles from "./UploadResultPopup.module.scss";

type ConfirmPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  onResumeExam: () => void;
  onSubmitExam: () => void;
  isSubmitting?: boolean;
  hideResume?: boolean;
};

export const ConfirmPopup = ({
  isOpen,
  onClose,
  onResumeExam,
  onSubmitExam,
  isSubmitting = false,
  hideResume = false,
}: ConfirmPopupProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation("tests");
  const isUltraCompactWidth = useIsMobile(320);
  const isCompactWidth = useIsMobile(360);
  const compactButtonProps = isUltraCompactWidth
    ? { size: "xs" as const, fontSize: 11, paddingX: 4 }
    : isCompactWidth
      ? { size: "sm" as const, fontSize: 12, paddingX: 5 }
      : { size: "md" as const };

  return (
    <TestModal
      isOpen={isOpen}
      onClose={onClose}
      width="min(100vw, 40rem)"
      body={
        <VStack className={styles.confirmBody}>
          <HStack justifyContent="flex-end" className={styles.confirmHeader}>
            <RippleButton
              variant="text"
              shape="round"
              {...compactButtonProps}
              onPress={onClose}
              iconOnly
            >
              <Icon name="close" size={28} color={theme.colors.primary[40]} />
            </RippleButton>
          </HStack>

          <VStack alignItems="center" className={styles.confirmContent}>
            <Text className={styles.title}>
              {t("upload_answer_sheet_flow.confirm_submission_title")}
            </Text>

            <Text
              variant="body"
              size="large"
              weight="emphasized"
              color={theme.colors.neutral[20]}
              align="center"
              className={styles.confirmText}
            >
              {t("upload_answer_sheet_flow.confirm_submission_message")}
            </Text>
          </VStack>

          <HStack className={styles.confirmActions}>
            {!hideResume && (
              <RippleButton
                variant="text"
                {...compactButtonProps}
                shape="round"
                fullWidth
                onPress={onResumeExam}
                className={styles.buttonText}
              >
                {t("upload_answer_sheet_flow.resume_exam")}
              </RippleButton>
            )}

            <RippleButton
              variant="primary"
              {...compactButtonProps}
              color="success"
              backgroundColor={theme.colors.buttonSuccess}
              shape="round"
              fullWidth
              onPress={onSubmitExam}
              disabled={isSubmitting}
              loading={isSubmitting}
              className={styles.buttonText}
            >
              {t("upload_answer_sheet_flow.submit_exam")}
            </RippleButton>
          </HStack>
        </VStack>
      }
    />
  );
};
